/**
 * GlassesManager — model cache, swap, and scene integration.
 *
 * Responsibilities:
 *  - Cache loaded GLTF models by product ID to avoid redundant network requests
 *  - Expose a clean swap API (swap) that handles dispose + load atomically
 *  - Surface the active product ID for UI binding
 *
 * Usage:
 *   const manager = new GlassesManager();
 *   await manager.preload('frame-001', 'https://cdn.example.com/frame-001.glb');
 *   await manager.swap('frame-001', scene);
 *   manager.destroy();
 */

import type { TryonScene } from './scene.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Cached entry for a successfully preloaded model URL. */
interface CacheEntry {
  url: string;
  /** Timestamp of when this entry was last accessed (for potential LRU eviction). */
  lastAccessed: number;
}

// ---------------------------------------------------------------------------
// GlassesManager
// ---------------------------------------------------------------------------

export class GlassesManager {
  /** Maps productId → cached URL entry. */
  private readonly cache = new Map<string, CacheEntry>();

  /** The productId of the currently displayed model. */
  private currentProductId: string | null = null;

  /** Whether a swap is in progress (prevents concurrent swaps). */
  private swapping = false;

  /** Serializes model swaps so rapid product clicks resolve in request order. */
  private swapQueue: Promise<void> = Promise.resolve();

  /** Incremented on destroy() to fence stale queued async work. */
  private generation = 0;

  // ── Cache management ────────────────────────────────────────────────────

  /**
   * Pre-register a model URL for a product ID.
   *
   * This does NOT eagerly load the GLB — it stores the URL so that `swap()`
   * can call scene.loadModel() without needing to know the URL separately.
   * Call this during widget initialisation for instant swaps when the user
   * browses the product catalog.
   *
   * @param productId - Unique product identifier (matches Product.id).
   * @param url       - URL of the .glb model file.
   */
  preload(productId: string, url: string): void {
    this.cache.set(productId, { url, lastAccessed: Date.now() });
  }

  // ── Model swap ──────────────────────────────────────────────────────────

  /**
   * Load and display a product's GLB model in the given scene.
   *
   * If the product is already active when this request runs, this is a no-op.
   * Concurrent swap calls are serialized so a rapid second click is not dropped.
   *
   * @param productId - Must match a previously preloaded product ID.
   * @param scene     - The TryonScene to load the model into.
   * @throws Error if productId was not preloaded.
   */
  async swap(productId: string, scene: TryonScene): Promise<void> {
    const requestGeneration = this.generation;

    const runSwap = async () => {
      if (requestGeneration !== this.generation) return;

      // Already showing this product by the time the queued request runs.
      if (this.currentProductId === productId) return;

      const entry = this.cache.get(productId);
      if (!entry) {
        throw new Error(
          `[GlassesManager] No preloaded URL for product "${productId}". ` +
            'Call preload(productId, url) before swap().',
        );
      }

      this.swapping = true;
      entry.lastAccessed = Date.now();

      try {
        await scene.loadModel(entry.url);
        if (requestGeneration !== this.generation) return;
        this.currentProductId = productId;
      } finally {
        if (requestGeneration === this.generation) {
          this.swapping = false;
        }
      }
    };

    const queued = this.swapQueue.then(runSwap, runSwap);
    this.swapQueue = queued.catch(() => { /* keep queue alive after a failed load */ });
    return queued;
  }

  /**
   * Load a model directly from a URL without requiring a prior preload() call.
   *
   * Also registers the URL in the cache for future swap() calls.
   *
   * @param productId - Product identifier (used for caching).
   * @param url       - Model URL.
   * @param scene     - The TryonScene to load into.
   */
  async swapByUrl(productId: string, url: string, scene: TryonScene): Promise<void> {
    this.preload(productId, url);
    await this.swap(productId, scene);
  }

  // ── State accessors ─────────────────────────────────────────────────────

  /**
   * Returns the product ID of the currently displayed model, or null if none
   * has been loaded yet.
   */
  getCurrent(): string | null {
    return this.currentProductId;
  }

  /**
   * Returns true if a model is currently loading (swap in progress).
   */
  get isLoading(): boolean {
    return this.swapping;
  }

  /**
   * Returns all preloaded product IDs.
   */
  getPreloadedIds(): string[] {
    return Array.from(this.cache.keys());
  }

  // ── Cleanup ─────────────────────────────────────────────────────────────

  /**
   * Clear the URL cache without affecting the currently displayed model.
   * Subsequent swap() calls will fail unless preload() is called again.
   */
  clearCache(): void {
    this.cache.clear();
    // currentProductId intentionally kept — the model is still in the scene
  }

  /**
   * Reset the manager completely.
   *
   * Does NOT dispose scene resources — that is the responsibility of the
   * TryonScene instance. Call scene.destroy() separately if needed.
   */
  destroy(): void {
    this.generation += 1;
    this.cache.clear();
    this.currentProductId = null;
    this.swapping = false;
    this.swapQueue = Promise.resolve();
  }
}
